import React, { useContext } from 'react';
import styled from 'styled-components';

import CheckboxSettingsField from './CheckboxSettingsField';
import InputSettingsField from './InputSettingsField';
import IdentifierSelectorSettings from './IdentifierSelectorSettings';
import FieldStyleSettings from './FieldStyleSettings';
import PositionSettings from './PositionSettings';
import MetadataSettings from './MetadataSettings';
import ButtonGroupSettings from './ButtonGroupSettings';
import FieldSettingsHeaderTitle from './FieldSettingsHeaderTitle';
import Button from '../../misc/Button';
import ConditionalLogicSettings from './ConditionalLogicSettings';
import SettingsTabs from './SettingsTabs';
import TooltipSettings from './TooltipSettings';
import AccordionSettings from './AccordionSettings';
import SettingsLabel from './SettingsLabel';
import TitleSettings from './TitleSettings';
import { StyledFieldWrapper, StyledButtonsWrapper, DividerLine } from './SettingsCommonElements';

import FieldTypes from '../../../constants/FieldTypes';
import ThemeIcons from '../../../theme/Icons';
import ThemeContext from '../../../theme/ThemeContext';

const StyledWrapper = styled.div`
  padding: 12px 0px;
  display: flex;
  flex-direction: column;
`;

/**
 * These field settings should be used for the following fields:
 * - text
 * - textarea
 * - date
 * - number
 * - uniqueId
 */
const FieldSettingsTextField = ({

  //Logic properties
  fileId,
  sortedPages,
  fields,
  logic,

  view,
  fieldId,
  fieldIdentifier,
  fieldPositionId,

  title,
  type,
  format,
  x,
  y,
  width,
  height,
  value,
  metadata,
  config,
  hidden,

  //API Developers
  identifier,
  required,

  //Title styles
  titleDisplay,
  titleFontSize,
  titleFontColor,
  titleTextAlign,
  titleFontStyle,
  titleFontWeight,
  titleTextTransform,
  titleTextDecoration,

  //Field style
  fontSize,
  fontColor,
  fontStyle,
  fontWeight,
  textAlign,
  textTransform,
  textDecoration,
  textOverflow,
  borderColor,
  borderRadius,
  borderWidth,
  backgroundColor,
  padding,
  lineHeight,
  zIndex,

  //Action handlers
  onChange,
  onDelete,

  tipVisible,
  tipTitle,
  tipDescription,

}) => {

  const theme = useContext(ThemeContext);

  let nextFieldSettingsIcon = '';
  if (type === FieldTypes.text) nextFieldSettingsIcon = ThemeIcons.fields.text;
  if (type === FieldTypes.number) nextFieldSettingsIcon = ThemeIcons.fields.number;
  if (type === FieldTypes.date) nextFieldSettingsIcon = ThemeIcons.fields.date;
  if (type === FieldTypes.textarea) nextFieldSettingsIcon = ThemeIcons.fields.textarea;

  const defaultValueNode = type !== FieldTypes.text && type !== FieldTypes.textarea ? null : (
    <StyledFieldWrapper>
      <InputSettingsField
        label="Default"
        fieldId={fieldId}
        value={value}
        type={type}
        rows={3}
        onChange={(fieldId, value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { value })}
      />
    </StyledFieldWrapper>
  );

  const formatNode = type !== FieldTypes.date ? null : (
    <StyledFieldWrapper style={{ marginBottom: 0 }}>
      <ButtonGroupSettings
        id={fieldId}
        types={[
          {
            type: 'MM/DD/YYYY',
            icon: ThemeIcons.settings.formatDate,
            active: format === 'MM/DD/YYYY',
          },
          {
            type: 'MM/DD/YYYY hh:mma',
            icon: ThemeIcons.settings.formatDateTime,
            active: format === 'MM/DD/YYYY hh:mma',
          },
          {
            type: 'hh:mma',
            icon: ThemeIcons.settings.formatTime,
            active: format === 'hh:mma',
          }
        ]}
        onChange={(value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { format: value })}
      />
    </StyledFieldWrapper>
  );

  const stylesNode = (
    <>
      <AccordionSettings
        isOpen
        title="Title"
        height={170}
      >
        <FieldStyleSettings
          id={`${fieldId}-title`}
          {...{
            fontSize: titleFontSize,
            onFontSizeChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleFontSize: value }),

            fontColor: titleFontColor,
            onFontColorChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleFontColor: value }),

            textAlign: titleTextAlign,
            onTextAlignChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleTextAlign: value }),

            fontStyle: titleFontStyle,
            onFontStyleChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleFontStyle: value }),

            fontWeight: titleFontWeight,
            onFontWeightChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleFontWeight: value }),

            textTransform: titleTextTransform,
            onTextTransformChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleTextTransform: value }),

            textDecoration: titleTextDecoration,
            onTextDecorationChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleTextDecoration: value }),
          }}
        />
      </AccordionSettings>
      <DividerLine />
      <AccordionSettings
        isOpen
        title="Text"
        height={type === FieldTypes.textarea ? 210 : 170}
      >
        <FieldStyleSettings
          id={`${fieldId}-field`}
          {...{

            lineHeight: lineHeight || 10,
            onLineHeightChange: type !== FieldTypes.textarea ? null : (value) => {
              if (value) onChange(fieldId, fieldIdentifier, fieldPositionId, { lineHeight: value });
            },

            fontSize,
            onFontSizeChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { fontSize: value }),

            fontColor,
            onFontColorChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { fontColor: value }),

            textAlign,
            onTextAlignChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { textAlign: value }),

            fontStyle,
            onFontStyleChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { fontStyle: value }),

            fontWeight,
            onFontWeightChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { fontWeight: value }),

            textTransform,
            onTextTransformChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { textTransform: value }),

            textDecoration,
            onTextDecorationChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { textDecoration: value }),

            textOverflow,
            onTextOverflowChange: type === FieldTypes.textarea ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { textOverflow: value }),

          }}
        />
      </AccordionSettings>
      <DividerLine />
      <AccordionSettings
        isOpen
        title="Field"
        height={320}
      >
        <FieldStyleSettings
          id={`${fieldId}-field`}
          {...{

            borderColor,
            onBorderColorChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { borderColor: value }),

            borderRadius,
            onBorderRadiusChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { borderRadius: value }),

            borderWidth,
            onBorderWidthChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { borderWidth: value }),

            backgroundColor,
            onBackgroundColorChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { backgroundColor: value }),

            padding,
            onPaddingChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { padding: value }),

            zIndex,
            onZIndexChange: (value) => (value ? onChange(fieldId, fieldIdentifier, fieldPositionId, { zIndex: value }) : ''),

          }}
        />
        <PositionSettings
          {...{
            view,
            x,
            y,
            width,
            height,
            onChange: (field, value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { [field]: value })
          }}
        />
      </AccordionSettings>
    </>
  );

  const settingsNode = (
    <>
      <SettingsLabel>Title</SettingsLabel>
      <DividerLine style={{ marginBottom: 14 }} />
      <TitleSettings
        {...{
          title,
          titleDisplay
        }}
        onChange={(changes) => onChange(fieldId, fieldIdentifier, fieldPositionId, changes)}
      />
      <SettingsLabel>Field</SettingsLabel>
      <DividerLine style={{ marginBottom: 14 }} />
      {defaultValueNode}
      {formatNode}
      <StyledFieldWrapper>
        <CheckboxSettingsField
          icon={ThemeIcons.settings.required}
          iconColor={required ? theme.dangerColor : theme.borderColor}
          iconSize="1x"
          id="required"
          label="Required"
          selected={required}
          onChange={(selected) => onChange(fieldId, fieldIdentifier, fieldPositionId, { required: selected },)}
        />
      </StyledFieldWrapper>
      <StyledFieldWrapper>
        <CheckboxSettingsField
          icon={ThemeIcons.settings.hide}
          iconColor={hidden ? theme.warningColor : theme.borderColor}
          id="hidden"
          label="Hidden"
          selected={hidden}
          onChange={(selected) => onChange(fieldId, fieldIdentifier, fieldPositionId, { hidden: selected },)}
        />
      </StyledFieldWrapper>
      {fields.length < 2 ? null : (
        <>
          <DividerLine />
          <StyledFieldWrapper>
            <ConditionalLogicSettings
              fileId={fileId}
              targetLabel=" field"
              targetFieldId={fieldId}
              sortedPages={sortedPages}
              fields={fields}
              logic={logic}
              onSubmit={(nextLogic) => onChange(fieldId, fieldIdentifier, fieldPositionId, { logic: nextLogic })}
            />
          </StyledFieldWrapper>
        </>
      )}
      {!config?.metadata ? null : (
        <>
          <DividerLine />
          <MetadataSettings
            metadata={metadata}
            metadataOptions={config?.metadata}
            onChange={(changes) => {
              const nextMetadata = metadata ? { ...metadata, ...changes } : { ...changes };
              onChange(fieldId, fieldIdentifier, fieldPositionId, { metadata: nextMetadata });
            }}
          />
        </>
      )}
      <DividerLine style={{ marginBottom: 0 }} />
      <AccordionSettings
        title="Tooltip"
        height={166}
      >
        <TooltipSettings
          {...{
            tipVisible,
            tipTitle,
            tipDescription
          }}
          onChange={(changes) => onChange(fieldId, fieldIdentifier, fieldPositionId, changes)}
        />
      </AccordionSettings>
      <DividerLine style={{ marginBottom: 0 }} />
      <AccordionSettings
        title={config?.identifier?.label || 'Identifier'}
        height={60}
      >
        <IdentifierSelectorSettings
          fieldId={fieldId}
          fieldType={type}
          type="text"
          customValue={config?.identifier?.options}
          value={identifier}
          onChange={(fieldId, value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { identifier: value })}
        />
      </AccordionSettings>
      <DividerLine style={{ marginBottom: 14 }} />
      <StyledButtonsWrapper>
        <Button
          danger
          icon={ThemeIcons.settings.delete}
          text="Delete"
          onClick={() => onDelete(fieldId, fieldIdentifier, fieldPositionId)}
        />
      </StyledButtonsWrapper>
    </>
  );

  return (
    <StyledWrapper data-testid="form-settings">
      <FieldSettingsHeaderTitle
        title="Settings"
        icon={nextFieldSettingsIcon}
      />
      <SettingsTabs
        settings={settingsNode}
        styles={stylesNode}
      />
    </StyledWrapper>
  );

};

export default FieldSettingsTextField;
